import type { ControlPosition } from "mapbox-gl";

import { Minus, Plus, Target03 } from "@untitledui/icons";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { useSettings } from "@/hooks/useSettings";
import { ContrastModes } from "@/types/settings";
import { cx } from "@/utils/cx";

import {
	coordinatesFromMapFitFeature,
	isValidCoordinatePair,
} from "../utils/shared/coordinates";
import {
	getMapboxAccessToken,
	MAPBOX_DARK_SATELLITE_STYLE,
	MAPBOX_DARK_STREET_STYLE,
	MAPBOX_LIGHT_SATELLITE_STYLE,
	MAPBOX_LIGHT_STREET_STYLE,
} from "./mapbox-constants";

/** Stub: PostHog map analytics source (unused in shadow-realm). */
type MapMarkerClickSource = string;

/** Stub: dashboard flash alerts are out of scope for Map Configuration. */
type MarkerFlashState = {
	alertCount?: number;
	isFlashing?: boolean;
	latestSeverity?: number;
	severity?: number;
};
import type { FloorMapImage } from "./floors/floor-map-images";

import {
	useMapboxDraw,
	type ZoneBoundaryDrawConfig,
} from "./draw/useMapboxDraw";
import { useFloorImages } from "./floors/useFloorImages";
import {
	isClusterMarkerFeature,
	isSensorMarkerFeature,
} from "./markers/create-marker-feature";
import { MapMarker } from "./markers/map-marker";
import { MapMarkerPopup } from "./markers/map-marker-popup";
import {
	MAP_OVERLAY_FILL_LAYER_ID,
	MAP_OVERLAY_LINE_LAYER_ID,
	useMapOverlays,
} from "./overlays/useMapOverlays";

const DEFAULT_CENTER: [number, number] = [-104.9903, 39.7392];
const DEFAULT_ZOOM = 16;
const DEFAULT_PADDING = 150;
const DEFAULT_PITCH = 30;
/** Extra top inset so an open pin popup stays on-screen when we center on that pin. */
const POPUP_FIT_TOP_EXTRA = 140;
const ZERO_CAMERA_PADDING: mapboxgl.PaddingOptions = {
	bottom: 0,
	left: 0,
	right: 0,
	top: 0,
};
/** Match the map-config sidebar slide so padding eases with the drawer. */
const CAMERA_PADDING_DURATION_MS = 300;

type FitMapOptions = {
	animated?: boolean;
	/**
	 * Persistent panel padding (e.g. overlay sidebar) merged into the fit so
	 * `easeTo`/`fitBounds` do not wipe the sidebar offset.
	 */
	cameraPadding?: mapboxgl.PaddingOptions;
	includePopupOffset?: boolean;
	/** Cap zoom when fitting a single point (fitBounds degenerates and zooms out) */
	maxFitZoom?: number;
	pitch?: number;
	/** Zoom used when fitting a single point (fitBounds degenerates and zooms out) */
	singleMarkerZoom?: number;
};

const toPaddingOptions = (
	padding: mapboxgl.PaddingOptions | number,
): mapboxgl.PaddingOptions =>
	typeof padding === "number"
		? { bottom: padding, left: padding, right: padding, top: padding }
		: padding;

const mergeFitPadding = (
	fitPadding: mapboxgl.PaddingOptions | number,
	cameraPadding?: mapboxgl.PaddingOptions,
): mapboxgl.PaddingOptions => {
	const fit = toPaddingOptions(fitPadding);
	return {
		bottom: (fit.bottom ?? 0) + (cameraPadding?.bottom ?? 0),
		left: (fit.left ?? 0) + (cameraPadding?.left ?? 0),
		right: (fit.right ?? 0) + (cameraPadding?.right ?? 0),
		top: (fit.top ?? 0) + (cameraPadding?.top ?? 0),
	};
};

const getFitBoundsPadding = (
	containerWidth: number,
	containerHeight: number,
	includePopupOffset: boolean,
): mapboxgl.PaddingOptions | number => {
	const base =
		containerWidth > 400 && containerHeight > 400 ? DEFAULT_PADDING : 20;

	if (!includePopupOffset) return base;

	return {
		bottom: base,
		left: base,
		right: base,
		top: base + POPUP_FIT_TOP_EXTRA,
	};
};

/**
 * Dark street styles are globe projections with terrain. `cameraForBounds`
 * folds ground elevation into the frame, and `easeTo` recenters on that
 * terrain when the animation ends. Cached terrain arrives during the fit, so
 * the camera zooms in and then backs out — a little on the all-locations
 * view, and out to the neighborhood once a floor is selected. Mercator
 * without terrain keeps the fit on the features.
 */
const flattenStyleCamera = (map: mapboxgl.Map) => {
	if (map.getProjection()?.name !== "mercator") {
		map.setProjection("mercator");
	}

	if (map.getTerrain()) {
		map.setTerrain(null);
	}
};

const FIT_ANIMATION_DURATION_MS = 600;
/** Zoom levels to ease through on the first fit when `animateInitialFit` is set */
const INITIAL_FIT_ZOOM_DELTA = 3;
const INITIAL_FIT_MIN_ZOOM = 11;

const getSingleMarkerTargetZoom = (
	zoom: number | undefined,
	singleMarkerZoom: number | undefined,
): number => zoom ?? singleMarkerZoom ?? DEFAULT_ZOOM;

const getSingleMarkerInitialZoom = (
	targetZoom: number,
	animateInitialFit: boolean,
): number =>
	animateInitialFit
		? Math.max(targetZoom - INITIAL_FIT_ZOOM_DELTA, INITIAL_FIT_MIN_ZOOM)
		: targetZoom;

const fitMapToCoordinates = (
	map: mapboxgl.Map,
	markerData: GeoJSON.Feature[],
	options: FitMapOptions = {},
): boolean => {
	if (markerData.length === 0) return false;

	const animated = options.animated ?? true;
	const duration = animated ? FIT_ANIMATION_DURATION_MS : 0;
	const pitch = options.pitch ?? DEFAULT_PITCH;
	const maxZoom = options.maxFitZoom ?? 20;
	const persistentPadding = options.cameraPadding ?? ZERO_CAMERA_PADDING;

	// Points, zone polygons, and line boundaries all contribute to the frame.
	const validCoords = markerData.flatMap(coordinatesFromMapFitFeature);

	if (validCoords.length === 0) return false;

	// A lone point has zero-area bounds; fitBounds zooms far out. Center and
	// set an explicit zoom instead. Keep panel cameraPadding as-is — do not
	// fold in DEFAULT_PADDING or easeTo will replace the sidebar offset and
	// shove Mapbox controls / the vanishing point into the drawer.
	if (validCoords.length === 1) {
		const [lng, lat] = validCoords[0]!;
		const singleZoom = Math.min(
			options.singleMarkerZoom ?? DEFAULT_ZOOM,
			options.maxFitZoom ?? Number.POSITIVE_INFINITY,
		);
		try {
			const padding: mapboxgl.PaddingOptions = {
				bottom: persistentPadding.bottom ?? 0,
				left: persistentPadding.left ?? 0,
				right: persistentPadding.right ?? 0,
				top:
					(persistentPadding.top ?? 0) +
					(options.includePopupOffset ? POPUP_FIT_TOP_EXTRA : 0),
			};

			map.easeTo({
				center: [lng, lat],
				duration,
				easing: (t: number) => t * (2 - t),
				padding,
				pitch,
				zoom: singleZoom,
			});
			return true;
		} catch (error) {
			console.warn("Failed to center map on marker:", error);
			map.setCenter([lng, lat]);
			map.setZoom(singleZoom);
			return true;
		}
	}

	const bounds = new mapboxgl.LngLatBounds();

	validCoords.forEach(([lng, lat]) => {
		bounds.extend([lng, lat]);
	});

	if (bounds.isEmpty()) return false;

	const container = map.getContainer();
	const aestheticPadding = getFitBoundsPadding(
		container.clientWidth,
		container.clientHeight,
		options.includePopupOffset ?? false,
	);

	// Try full aesthetic + sidebar padding first; if the inset is too large for
	// the canvas (common with the overlay drawer), fall back to sidebar-only.
	const paddingCandidates: mapboxgl.PaddingOptions[] = [
		mergeFitPadding(aestheticPadding, persistentPadding),
		mergeFitPadding(20, persistentPadding),
		{ ...ZERO_CAMERA_PADDING, ...persistentPadding },
	];

	try {
		for (const padding of paddingCandidates) {
			const camera = map.cameraForBounds(bounds, {
				maxZoom,
				padding,
				pitch,
			});
			if (!camera) continue;

			map.easeTo({
				bearing: camera.bearing,
				center: camera.center,
				duration,
				easing: (t: number) => t * (2 - t),
				padding,
				pitch: camera.pitch ?? pitch,
				zoom: camera.zoom,
			});
			return true;
		}
	} catch (error) {
		console.warn("Failed to fit map bounds:", error);
		const firstCoord = validCoords[0];
		if (firstCoord) {
			map.setCenter(firstCoord);
			map.setZoom(DEFAULT_ZOOM);
			return true;
		}
	}

	return false;
};

/** Shared by initial load and the recenter control so both frame markers the same way. */
const getVisibleMarkerFitOptions = ({
	animated,
	cameraPadding,
	includePopupOffset,
	maxFitZoom,
	pitch,
	singleMarkerZoom,
}: {
	animated: boolean;
	cameraPadding?: mapboxgl.PaddingOptions;
	includePopupOffset: boolean;
	maxFitZoom?: number;
	pitch?: number;
	singleMarkerZoom?: number;
}): FitMapOptions => ({
	animated,
	cameraPadding,
	includePopupOffset,
	pitch: pitch ?? DEFAULT_PITCH,
	singleMarkerZoom:
		singleMarkerZoom ?? (maxFitZoom !== undefined ? maxFitZoom : undefined),
});

interface MapProps {
	/** When `null`, explicitly hides any popup (controlled mode). Omit for internal state. */
	activeFeature?: GeoJSON.Feature | null;
	/** Identifies which page map the marker click came from for PostHog */
	analyticsSource?: MapMarkerClickSource;
	/** When true, the first marker fit eases into place; recenter always animates */
	animateInitialFit?: boolean;
	attributionControl?: boolean;
	bearing?: number;
	/**
	 * Persistent camera padding (e.g. right inset for an overlay sidebar).
	 * Animates when changed so the focal point stays in the visible map area.
	 */
	cameraPadding?: mapboxgl.PaddingOptions;
	center?: [number, number];
	className?: string;
	/** Use min-h-0 instead of min-h-80 for embedded thumbnails */
	compact?: boolean;
	/**
	 * Location-cluster marker ids (`location-cluster:{locationId}`) to scale up
	 * while hovering a row in the locations sidebar.
	 */
	emphasizedLocationClusterIds?: ReadonlySet<string>;
	/**
	 * Sensor pin ids to scale up (alert-list / sidebar hover). Ignored for cluster cards.
	 */
	emphasizedSensorIds?: ReadonlySet<string>;
	/** When set, bounds refit runs only when this key changes (not when marker coords change). */
	fitBoundsKey?: string;
	/**
	 * Markers used to frame the camera. Locate on a single sensor passes just
	 * that pin so we center on it instead of the whole zone.
	 */
	fitMarkers?: GeoJSON.Feature[] | GeoJSON.FeatureCollection;
	/** Floor plans drawn under zone overlays. */
	floorImages?: readonly FloorMapImage[];
	/**
	 * Open (or clear) a pin popup. Each Locate click bumps `requestId` so we
	 * can reopen the same pin after the operator closed it or used breadcrumbs.
	 */
	focusMarker?: {
		featureId: null | string;
		requestId: number;
	};
	hash?: boolean;
	/** Opens this marker popup on mount; popup state is then managed internally */
	initialActiveFeature?: GeoJSON.Feature;
	/** Disables pan/zoom and marker click handlers for embedded thumbnails */
	interactive?: boolean;
	logoPosition?: ControlPosition;
	/** Live-alert pulse, standing count, and priority color for each visible marker. */
	markerFlashes?: Record<string, MarkerFlashState>;
	markers?: GeoJSON.Feature[] | GeoJSON.FeatureCollection;
	/** When false, markers ignore clicks (and popups). Defaults to `interactive`. */
	markersInteractive?: boolean;
	/** Cap zoom when fitting a single marker. Multi-marker load and recenter fits are uncapped. */
	maxFitZoom?: number;
	minZoom?: number;
	/** Fired once after the first marker fit finishes (after animation if any) */
	onInitialFitComplete?: () => void;
	/** Fired when the user clicks the map (lng/lat). Useful for pick-a-point editing. */
	onMapClick?: (coordinates: [number, number]) => void;
	onMarkerClick?: (feature: GeoJSON.Feature) => void;
	/** Fired when the user right-clicks a sensor marker. */
	onMarkerContextMenu?: (
		feature: GeoJSON.Feature,
		point: { x: number; y: number },
	) => void;
	/** Fired when the user double-clicks a sensor marker. */
	onMarkerDoubleClick?: (feature: GeoJSON.Feature) => void;
	/**
	 * When set, markers with `properties.editable === true` can be dragged.
	 * Prefer this over `onMapClick` for moving pins without click-to-place.
	 */
	onMarkerDragEnd?: (
		feature: GeoJSON.Feature,
		coordinates: [number, number],
	) => void;
	/** Shown as a pencil on sensor popups when provided. */
	onMarkerEdit?: (feature: GeoJSON.Feature) => void;
	onMarkerHoverEnd?: (feature: GeoJSON.Feature) => void;
	onMarkerHoverStart?: (feature: GeoJSON.Feature) => void;
	/** Fired after pan/zoom settles with the current map center [lng, lat]. */
	onMoveEnd?: (center: [number, number]) => void;
	/** Fired when the user clicks a zone overlay polygon. */
	onOverlayClick?: (feature: GeoJSON.Feature) => void;
	/** Fired when the user right-clicks a zone overlay. */
	onOverlayContextMenu?: (
		feature: GeoJSON.Feature,
		point: { x: number; y: number },
	) => void;
	/** Fired when the user double-clicks a zone overlay. */
	onOverlayDoubleClick?: (feature: GeoJSON.Feature) => void;
	/** Fired when the operator closes a pin popup (not when switching pins). */
	onPopupClose?: () => void;
	/** Zone overlay clicks fall through so a floor edit is not replaced. */
	overlayPassthroughZoneClicks?: boolean;
	/**
	 * Zone overlay clicks for this id fall through (e.g. while Mapbox Draw is
	 * editing that zone's boundary).
	 */
	overlayPassthroughZoneId?: string;
	/** GeoJSON polygons/lines drawn beneath markers (e.g. zone bounding boxes). */
	overlays?: GeoJSON.Feature[] | GeoJSON.FeatureCollection;
	/** Enables click and pointer cursor on zone overlays. */
	overlaysInteractive?: boolean;
	pitch?: number;
	/** When false, only fit markers on the first load (not when marker coords change) */
	refitOnMarkerChange?: boolean;
	scrollZoom?: boolean;
	showControls?: boolean;
	/**
	 * When false, pin popups omit the Latest Event link (e.g. Event Details).
	 * Defaults to true.
	 */
	showLatestEventLink?: boolean;
	/** Zoom level when the map has exactly one marker */
	singleMarkerZoom?: number;
	style?: string;
	/** Keep marker selection styling without opening the pin popup. */
	suppressPopup?: boolean;
	/** Enables Mapbox Draw to edit a zone boundary polygon. */
	zoneBoundaryDraw?: ZoneBoundaryDrawConfig;
	zoom?: number;
}

export const Map = ({
	activeFeature: activeFeatureProp,
	analyticsSource,
	animateInitialFit = false,
	attributionControl,
	bearing,
	cameraPadding,
	center,
	className,
	compact = false,
	emphasizedLocationClusterIds,
	emphasizedSensorIds,
	fitBoundsKey,
	fitMarkers,
	floorImages,
	focusMarker,
	hash,
	initialActiveFeature,
	interactive = true,
	logoPosition,
	markerFlashes,
	markers,
	markersInteractive,
	maxFitZoom,
	minZoom,
	onInitialFitComplete,
	onMapClick,
	onMarkerClick,
	onMarkerContextMenu,
	onMarkerDoubleClick,
	onMarkerDragEnd,
	onMarkerEdit,
	onMarkerHoverEnd,
	onMarkerHoverStart,
	onMoveEnd,
	onOverlayClick,
	onOverlayContextMenu,
	onOverlayDoubleClick,
	onPopupClose,
	overlayPassthroughZoneClicks,
	overlayPassthroughZoneId,
	overlays,
	overlaysInteractive = false,
	pitch,
	refitOnMarkerChange = true,
	scrollZoom,
	showControls = true,
	showLatestEventLink = true,
	singleMarkerZoom,
	suppressPopup = false,
	zoneBoundaryDraw,
	zoom,
}: MapProps) => {
	const accessToken = getMapboxAccessToken();
	const areMarkersInteractive = markersInteractive ?? interactive;

	const mapRef = useRef<mapboxgl.Map | null>(null);
	const mapContainerRef = useRef<HTMLDivElement | null>(null);
	const resizeTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
	const hasInitialFitRef = useRef(false);
	const lastFitKeyRef = useRef<null | string>(null);
	const onInitialFitCompleteRef = useRef(onInitialFitComplete);
	// The initial-fit-complete notification must fire exactly once and survive
	// unrelated re-renders that re-run the fit effect. Track it via refs so the
	// pending timer is only cleared on unmount, never on effect re-runs.
	const initialFitTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null);
	const initialFitNotifiedRef = useRef(false);
	const onMapClickRef = useRef(onMapClick);
	const onMarkerDragEndRef = useRef(onMarkerDragEnd);
	const onMoveEndRef = useRef(onMoveEnd);
	const onPopupCloseRef = useRef(onPopupClose);
	useEffect(() => {
		onInitialFitCompleteRef.current = onInitialFitComplete;
		onMapClickRef.current = onMapClick;
		onMarkerDragEndRef.current = onMarkerDragEnd;
		onMoveEndRef.current = onMoveEnd;
		onPopupCloseRef.current = onPopupClose;
	}, [
		onInitialFitComplete,
		onMapClick,
		onMarkerDragEnd,
		onMoveEnd,
		onPopupClose,
	]);
	const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
	const [loadedStyleUrl, setLoadedStyleUrl] = useState<null | string>(null);
	const isMapInitializedRef = useRef(false);

	const [viewMode, setViewMode] = useState<"satellite" | "street">("street");

	// Set view mode to street by default
	useEffect(() => {
		const handleViewMode = () => {
			setViewMode("street");
		};
		handleViewMode();
	}, []);

	const { contrastMode } = useSettings();

	const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	});

	useEffect(() => {
		if (contrastMode !== ContrastModes.SYSTEM) return;

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = () => {
			setSystemPrefersDark(mediaQuery.matches);
		};
		handleChange();
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [contrastMode]);

	const isDarkTheme = useMemo(() => {
		if (contrastMode === ContrastModes.DARK) return true;
		if (contrastMode === ContrastModes.LIGHT) return false;
		return systemPrefersDark;
	}, [contrastMode, systemPrefersDark]);

	// Get the appropriate style URL based on theme and view mode
	const styleUrl = useMemo(() => {
		if (viewMode === "satellite") {
			return isDarkTheme
				? MAPBOX_DARK_SATELLITE_STYLE
				: MAPBOX_LIGHT_SATELLITE_STYLE;
		}
		return isDarkTheme ? MAPBOX_DARK_STREET_STYLE : MAPBOX_LIGHT_STREET_STYLE;
	}, [isDarkTheme, viewMode]);

	// Compute isMapLoaded based on whether the loaded style matches the current style
	// This avoids calling setState synchronously in effects - when styleUrl changes,
	// isMapLoaded automatically becomes false until setLoadedStyleUrl is called
	const isMapLoaded = loadedStyleUrl !== null && loadedStyleUrl === styleUrl;

	useMapOverlays(mapInstance, isMapLoaded, overlays, isDarkTheme, {
		interactive: overlaysInteractive,
		onOverlayClick,
		onOverlayContextMenu,
		onOverlayDoubleClick,
		passthroughZoneClicks: overlayPassthroughZoneClicks,
		passthroughZoneId: overlayPassthroughZoneId,
	});
	useFloorImages(mapInstance, isMapLoaded, floorImages);
	useMapboxDraw(mapInstance, isMapLoaded, zoneBoundaryDraw);

	const hasAppliedCameraPaddingRef = useRef(false);
	const lastCameraPaddingFingerprintRef = useRef<null | string>(null);

	// Offset the camera when a panel covers part of the map (overlay sidebar).
	// Track the prop fingerprint — not getPadding() — so fitBounds aesthetic
	// insets do not trigger a follow-up easeTo that shrinks padding and looks
	// like a zoom-out after framing a zone.
	useEffect(() => {
		if (!mapInstance || !isMapLoaded) return;

		const nextPadding = cameraPadding ?? ZERO_CAMERA_PADDING;
		const fingerprint = [
			nextPadding.top ?? 0,
			nextPadding.right ?? 0,
			nextPadding.bottom ?? 0,
			nextPadding.left ?? 0,
		].join(",");

		if (lastCameraPaddingFingerprintRef.current === fingerprint) {
			hasAppliedCameraPaddingRef.current = true;
			return;
		}
		lastCameraPaddingFingerprintRef.current = fingerprint;

		// First apply is instant so the initial marker fit already uses the
		// padded viewport; later toggles ease with the sidebar slide.
		if (!hasAppliedCameraPaddingRef.current) {
			hasAppliedCameraPaddingRef.current = true;
			mapInstance.setPadding(nextPadding);
			return;
		}

		mapInstance.easeTo({
			duration: CAMERA_PADDING_DURATION_MS,
			essential: true,
			padding: nextPadding,
		});
	}, [cameraPadding, isMapLoaded, mapInstance]);

	// Allow Mapbox double-click zoom on empty map space, but not on markers/zones.
	useEffect(() => {
		if (!mapInstance || !isMapLoaded) return;

		const preventZoomOnFeatures = (event: mapboxgl.MapMouseEvent) => {
			const target = event.originalEvent.target;
			if (target instanceof Element && target.closest(".mapboxgl-marker")) {
				event.preventDefault();
				return;
			}

			const overlayLayers = [
				MAP_OVERLAY_FILL_LAYER_ID,
				MAP_OVERLAY_LINE_LAYER_ID,
			].filter((layerId) => Boolean(mapInstance.getLayer(layerId)));
			if (overlayLayers.length === 0) return;

			const hits = mapInstance.queryRenderedFeatures(event.point, {
				layers: overlayLayers,
			});
			if (hits.length > 0) {
				event.preventDefault();
			}
		};

		mapInstance.on("dblclick", preventZoomOnFeatures);
		return () => {
			mapInstance.off("dblclick", preventZoomOnFeatures);
		};
	}, [isMapLoaded, mapInstance]);

	// Validate and normalize coordinates: Mapbox expects [longitude, latitude]
	const validateCoordinates = useCallback(
		(coords: [number, number] | undefined): [number, number] => {
			if (!isValidCoordinatePair(coords)) {
				if (coords) {
					console.warn(
						`Invalid coordinates: [${coords[0]}, ${coords[1]}]. Using default center.`,
					);
				}
				return DEFAULT_CENTER;
			}

			return coords;
		},
		[],
	);

	// Normalize markers to an array of features
	const markerFeatures = useMemo(() => {
		if (!markers) return [];
		if (Array.isArray(markers)) return markers;
		return markers.features || [];
	}, [markers]);

	const fitMarkerFeatures = useMemo(() => {
		const source = fitMarkers ?? markers;
		if (!source) return markerFeatures;
		if (Array.isArray(source)) return source;
		return source.features || [];
	}, [fitMarkers, markerFeatures, markers]);

	const markerFitKey = useMemo(
		() =>
			fitMarkerFeatures
				.map((feature) => {
					const id = String(feature.id ?? "");
					if (feature.geometry.type !== "Point") return `${id}:non-point`;
					return `${id}:${feature.geometry.coordinates.join(",")}`;
				})
				.join("|"),
		[fitMarkerFeatures],
	);

	useEffect(() => {
		lastFitKeyRef.current = null;
	}, [styleUrl]);

	// Validate center coordinates — fall back to the first marker when no center prop
	const validatedCenter = useMemo(() => {
		if (center) return validateCoordinates(center);

		const firstMarker = fitMarkerFeatures[0];
		if (firstMarker?.geometry.type === "Point") {
			const [lng, lat] = firstMarker.geometry.coordinates;
			if (
				typeof lng === "number" &&
				typeof lat === "number" &&
				isFinite(lng) &&
				isFinite(lat)
			) {
				return validateCoordinates([lng, lat]);
			}
		}

		return validateCoordinates(undefined);
	}, [center, fitMarkerFeatures, validateCoordinates]);

	// Use prop if provided, otherwise manage internally for backward compatibility
	const [internalActiveFeature, setInternalActiveFeature] = useState<
		GeoJSON.Feature | undefined
	>(initialActiveFeature);
	const [appliedFocusRequestId, setAppliedFocusRequestId] = useState<
		number | undefined
	>(undefined);
	// Locate opens or clears the internal popup. Remember the request id so a
	// closed popup is not forced open again until the next Locate click.
	let nextActiveFeature = internalActiveFeature;
	let nextAppliedFocusRequestId = appliedFocusRequestId;
	if (activeFeatureProp === undefined) {
		if (!focusMarker) {
			nextAppliedFocusRequestId = undefined;
		} else if (focusMarker.requestId !== appliedFocusRequestId) {
			if (focusMarker.featureId === null) {
				nextActiveFeature = undefined;
				nextAppliedFocusRequestId = focusMarker.requestId;
			} else {
				const focusedFeature = markerFeatures.find(
					(marker) => String(marker.id) === focusMarker.featureId,
				);
				if (focusedFeature) {
					if (String(internalActiveFeature?.id) !== focusMarker.featureId) {
						nextActiveFeature = focusedFeature;
					}
					nextAppliedFocusRequestId = focusMarker.requestId;
				}
			}
		}

		const popupFeature = nextActiveFeature;
		if (
			popupFeature &&
			(!areMarkersInteractive ||
				!markerFeatures.some((feature) => feature.id === popupFeature.id))
		) {
			nextActiveFeature = undefined;
		}
	}
	if (nextAppliedFocusRequestId !== appliedFocusRequestId) {
		setAppliedFocusRequestId(nextAppliedFocusRequestId);
	}
	if (nextActiveFeature !== internalActiveFeature) {
		setInternalActiveFeature(nextActiveFeature);
	}
	const activeFeature =
		activeFeatureProp !== undefined ? activeFeatureProp : internalActiveFeature;

	// Fit map bounds to markers if markers exist
	useEffect(() => {
		if (!mapRef.current || !isMapLoaded) return;

		const fitTriggerKey = fitBoundsKey ?? markerFitKey;
		if (lastFitKeyRef.current === fitTriggerKey) return;

		// Keep marker movement (e.g. pick-a-point editing) from re-centering the map
		if (!fitBoundsKey && !refitOnMarkerChange && hasInitialFitRef.current) {
			lastFitKeyRef.current = markerFitKey;
			return;
		}

		// Leave room above a single pin when a popup is (or will be) open.
		// `activeFeature={null}` is controlled "no popup" — only treat a real
		// feature (or focus request) as needing popup headroom.
		const includePopupOffset =
			fitMarkerFeatures.length === 1 &&
			(initialActiveFeature !== undefined ||
				activeFeatureProp != null ||
				Boolean(focusMarker?.featureId));

		const isInitialFit = !hasInitialFitRef.current;

		const canFitMapMarkerCoordinates = fitMapToCoordinates(
			mapRef.current,
			fitMarkerFeatures,
			getVisibleMarkerFitOptions({
				animated: isInitialFit ? animateInitialFit : true,
				cameraPadding,
				includePopupOffset,
				maxFitZoom,
				pitch,
				singleMarkerZoom,
			}),
		);

		// Only lock the key after we attempted a fit with real features.
		// Empty fit markers (zone overlays not ready yet) must retry when they
		// arrive instead of sticking at DEFAULT_ZOOM from the fallback below.
		if (canFitMapMarkerCoordinates) {
			hasInitialFitRef.current = true;
			lastFitKeyRef.current = fitTriggerKey;
		} else if (fitMarkerFeatures.length > 0) {
			hasInitialFitRef.current = true;
			lastFitKeyRef.current = fitTriggerKey;
		}

		// Notify once after the initial fit. Guard with a ref (not effect-scoped
		// state) so a re-render landing inside the animation window can't cancel
		// the pending timer and leave the callback permanently unfired.
		if (
			isInitialFit &&
			canFitMapMarkerCoordinates &&
			!initialFitNotifiedRef.current
		) {
			if (animateInitialFit) {
				initialFitTimerRef.current = setTimeout(() => {
					initialFitNotifiedRef.current = true;
					onInitialFitCompleteRef.current?.();
				}, FIT_ANIMATION_DURATION_MS);
			} else {
				initialFitNotifiedRef.current = true;
				onInitialFitCompleteRef.current?.();
			}
		}

		if (
			!canFitMapMarkerCoordinates &&
			fitMarkerFeatures.length > 0 &&
			validatedCenter
		) {
			mapRef.current.setCenter(validatedCenter);
			mapRef.current.setZoom(DEFAULT_ZOOM);
		}
	}, [
		activeFeatureProp,
		animateInitialFit,
		cameraPadding,
		fitBoundsKey,
		fitMarkerFeatures,
		focusMarker,
		initialActiveFeature,
		isMapLoaded,
		markerFeatures,
		markerFitKey,
		maxFitZoom,
		pitch,
		refitOnMarkerChange,
		singleMarkerZoom,
		validatedCenter,
	]);

	// Update map center when center prop changes (only if no markers). Skip
	// while a controlled fitBoundsKey is active with empty features — the fit
	// effect will frame once markers arrive instead of flashing DEFAULT_ZOOM.
	useEffect(() => {
		if (!mapRef.current || !validatedCenter || fitMarkerFeatures.length > 0)
			return;
		if (fitBoundsKey) return;
		mapRef.current.setCenter(validatedCenter);
		mapRef.current.setZoom(DEFAULT_ZOOM);
	}, [fitBoundsKey, fitMarkerFeatures.length, validatedCenter]);

	// Track the previous style URL to avoid unnecessary re-renders
	const previousStyleUrlRef = useRef<null | string>(null);

	// Update map style when style URL changes.
	// `loaded()` stays true until the new style replaces the old one, and
	// subsequent swaps fire `style.load` (not `load`). Treating either as
	// "ready" too early adds overlays that the incoming style then wipes.
	useEffect(() => {
		const map = mapRef.current;
		if (!map) return;

		if (previousStyleUrlRef.current === styleUrl) return;

		map.setStyle(styleUrl);
		previousStyleUrlRef.current = styleUrl;

		const handleStyleLoad = () => {
			flattenStyleCamera(map);
			map.resize();
			setLoadedStyleUrl(styleUrl);
		};

		map.once("style.load", handleStyleLoad);
		return () => {
			map.off("style.load", handleStyleLoad);
		};
	}, [styleUrl]);

	// Initialize map only once - subsequent prop changes are handled by separate effects
	useEffect(() => {
		if (isMapInitializedRef.current) return;

		mapboxgl.accessToken = accessToken;

		const singleMarkerTargetZoom =
			markerFeatures.length === 1
				? getSingleMarkerTargetZoom(zoom, singleMarkerZoom)
				: undefined;
		const initialZoom =
			singleMarkerTargetZoom !== undefined
				? getSingleMarkerInitialZoom(singleMarkerTargetZoom, animateInitialFit)
				: (zoom ?? 5.5);

		mapRef.current = new mapboxgl.Map({
			attributionControl: attributionControl || false, // Whether to show the attribution control
			bearing: bearing || 0, // The bearing angle of the map in degrees counter-clockwise from north
			center: validatedCenter, // The center coordinates of the map
			container: mapContainerRef.current as HTMLElement, // The container element to render the map into
			hash: hash || false, // Updates the URL hash with the current map state
			interactive, // Pan/zoom/rotate; off for list-card previews
			logoPosition: logoPosition || "bottom-left", // Position of the Mapbox logo
			minZoom: minZoom || 0, // The minimum zoom level of the map
			pitch: pitch ?? DEFAULT_PITCH, // The pitch angle of the map in degrees
			// Keep the style document from switching the camera to globe before
			// the first fit. The style-load handler drops terrain the same way.
			projection: "mercator",
			scrollZoom: scrollZoom ?? true, // Allows the map to be zoomed by scrolling
			style: styleUrl, // The style URL to use for the map
			zoom: initialZoom,
		});

		// The constructor already requested this style. Mark it applied now so
		// the style effect does not call setStyle again and replay the load.
		previousStyleUrlRef.current = styleUrl;
		isMapInitializedRef.current = true;

		// Set map instance immediately so we can track it
		setMapInstance(mapRef.current);

		const handleMapLoad = () => {
			previousStyleUrlRef.current = styleUrl;
			if (mapRef.current) flattenStyleCamera(mapRef.current);
			// Resize the map to fill its container after load
			mapRef.current?.resize();

			setLoadedStyleUrl(styleUrl);
		};

		// Check if map is already loaded (can happen if style loads quickly)
		if (mapRef.current.loaded()) {
			handleMapLoad();
		} else {
			mapRef.current.once("load", handleMapLoad);
		}

		// Debounce resize for window/browser size changes. Avoid calling
		// map.resize() every frame — that clears the WebGL canvas and flashes white.
		const resizeObserver = new ResizeObserver(() => {
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
			}
			resizeTimeoutRef.current = setTimeout(() => {
				mapRef.current?.resize();
			}, 150);
		});

		if (mapContainerRef.current) {
			resizeObserver.observe(mapContainerRef.current);
		}

		return () => {
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
				resizeTimeoutRef.current = null;
			}
			// Clear the pending initial-fit-complete timer on unmount only.
			if (initialFitTimerRef.current !== null) {
				clearTimeout(initialFitTimerRef.current);
				initialFitTimerRef.current = null;
			}
			resizeObserver.disconnect();
			mapRef.current?.remove();
			setMapInstance(null);
			setLoadedStyleUrl(null);
			isMapInitializedRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally empty: map initialization runs once on mount
	}, []);

	// Pick-a-point: fire onMapClick with [lng, lat] and show a crosshair cursor
	const isMapClickEnabled = onMapClick != null;
	useEffect(() => {
		if (!mapInstance || !interactive || !isMapClickEnabled) return;

		const canvas = mapInstance.getCanvas();
		const handleClick = (event: mapboxgl.MapMouseEvent) => {
			// HTML markers sit in the map container; a pin click can still reach
			// this canvas handler. Treat those as marker clicks, not pick-a-point.
			const target = event.originalEvent.target;
			if (target instanceof Element && target.closest(".mapboxgl-marker")) {
				return;
			}

			onMapClickRef.current?.([event.lngLat.lng, event.lngLat.lat]);
		};

		mapInstance.on("click", handleClick);
		canvas.style.cursor = "crosshair";

		return () => {
			mapInstance.off("click", handleClick);
			canvas.style.cursor = "";
		};
	}, [interactive, isMapClickEnabled, mapInstance]);

	// Report the live viewport center after pan/zoom (and once on load).
	const isMoveEndEnabled = onMoveEnd != null;
	useEffect(() => {
		if (!mapInstance || !isMoveEndEnabled) return;

		const reportCenter = () => {
			const { lat, lng } = mapInstance.getCenter();
			onMoveEndRef.current?.([lng, lat]);
		};

		reportCenter();
		mapInstance.on("moveend", reportCenter);
		return () => {
			mapInstance.off("moveend", reportCenter);
		};
	}, [isMoveEndEnabled, mapInstance]);

	// Handle marker click
	const handleMarkerClick = (feature: GeoJSON.Feature) => {
		void analyticsSource;
		onMarkerClick?.(feature);

		// Parent-controlled active feature — don't touch internal popup state
		if (activeFeatureProp !== undefined) return;

		// Cluster drill handlers shouldn't suppress sensor popups
		if (!onMarkerClick || isSensorMarkerFeature(feature)) {
			setInternalActiveFeature(feature);
			return;
		}

		setInternalActiveFeature(undefined);
	};

	// Handle marker popup close
	const handlePopupClose = useCallback(() => {
		if (activeFeatureProp === undefined) {
			setInternalActiveFeature(undefined);
		}
		onPopupCloseRef.current?.();
	}, [activeFeatureProp]);

	const handleRecenter = () => {
		if (!mapRef.current) return;

		const canFitMapMarkerCoordinates = fitMapToCoordinates(
			mapRef.current,
			fitMarkerFeatures,
			getVisibleMarkerFitOptions({
				animated: true,
				cameraPadding,
				includePopupOffset: !!activeFeature,
				maxFitZoom,
				pitch,
				singleMarkerZoom,
			}),
		);
		if (!canFitMapMarkerCoordinates && validatedCenter) {
			mapRef.current.setCenter(validatedCenter);
			mapRef.current.setZoom(DEFAULT_ZOOM);
		}
	};

	const handleZoomIn = () => {
		mapRef.current?.zoomIn();
	};

	const handleZoomOut = () => {
		mapRef.current?.zoomOut();
	};

	return (
		<div
			className={cx(
				"relative h-full w-full overflow-hidden rounded-lg border border-secondary_alt",
				className,
			)}
		>
			<div
				className={cx(
					"h-full w-full",
					// Mapbox permits a transparent logo/attribution background per their guidelines.
					"[&_.mapboxgl-ctrl-attrib]:!bg-transparent [&_.mapboxgl-ctrl-attrib.mapboxgl-compact]:!bg-transparent [&_.mapboxgl-ctrl-logo]:bg-transparent",
					compact ? "min-h-0" : "min-h-80",
				)}
				id="map-container"
				ref={mapContainerRef}
			>
				{mapInstance &&
					isMapLoaded &&
					markerFeatures.map((feature: GeoJSON.Feature) => {
						const isEditableMarker = feature.properties?.editable === true;
						const markerInteractive = areMarkersInteractive;
						const markerDraggable =
							isEditableMarker &&
							(Boolean(onMarkerDragEnd) || isMapClickEnabled);

						const flash =
							feature.id == null
								? undefined
								: markerFlashes?.[String(feature.id)];
						const featureId = String(
							feature.id ?? feature.properties?.id ?? "",
						);
						const isEmphasized =
							(isSensorMarkerFeature(feature) &&
								Boolean(emphasizedSensorIds?.has(featureId))) ||
							(isClusterMarkerFeature(feature) &&
								Boolean(emphasizedLocationClusterIds?.has(featureId)));

						return (
							<MapMarker
								draggable={markerDraggable}
								feature={feature}
								flashAlertCount={flash?.alertCount}
								flashIsFlashing={flash?.isFlashing}
								flashSeverity={
									// Pins take the most recent live alert; clusters take the highest priority.
									isSensorMarkerFeature(feature)
										? flash?.latestSeverity
										: flash?.severity
								}
								interactive={markerInteractive}
								isActive={markerInteractive && activeFeature?.id === feature.id}
								isEmphasized={isEmphasized}
								key={feature.id || JSON.stringify(feature.geometry)}
								map={mapInstance}
								onClick={handleMarkerClick}
								onContextMenu={
									onMarkerContextMenu
										? (feature, point) => onMarkerContextMenu(feature, point)
										: undefined
								}
								onDoubleClick={
									onMarkerDoubleClick
										? () => onMarkerDoubleClick(feature)
										: undefined
								}
								onDragEnd={
									markerDraggable
										? (coordinates) => {
												if (onMarkerDragEndRef.current) {
													onMarkerDragEndRef.current(feature, coordinates);
													return;
												}
												onMapClickRef.current?.(coordinates);
											}
										: undefined
								}
								onHoverEnd={
									isSensorMarkerFeature(feature) && onMarkerHoverEnd
										? () => onMarkerHoverEnd(feature)
										: undefined
								}
								onHoverStart={
									isSensorMarkerFeature(feature) && onMarkerHoverStart
										? () => onMarkerHoverStart(feature)
										: undefined
								}
							/>
						);
					})}
				{mapInstance &&
					isMapLoaded &&
					areMarkersInteractive &&
					activeFeature &&
					!suppressPopup && (
						<MapMarkerPopup
							activeFeature={activeFeature}
							// Single-marker maps auto-open the popup, so don't steal focus;
							// multi-marker maps open via user click, where focus is fine.
							focusAfterOpen={markerFeatures.length > 1}
							isEmphasized={
								isSensorMarkerFeature(activeFeature) &&
								Boolean(
									emphasizedSensorIds?.has(
										String(
											activeFeature.id ?? activeFeature.properties?.id ?? "",
										),
									),
								)
							}
							map={mapInstance}
							onClose={handlePopupClose}
							onEdit={onMarkerEdit}
							showLatestEventLink={showLatestEventLink}
						/>
					)}
			</div>
			{mapInstance && isMapLoaded && showControls && (
				<div
					className="absolute bottom-[10px] z-10 flex flex-col gap-2 transition-[right] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
					// Keep zoom/recenter clear of an overlay sidebar — same inset
					// and timing as `cameraPadding` / the drawer slide.
					style={{ right: 10 + (cameraPadding?.right ?? 0) }}
				>
					<Button
						className="size-8"
						color="secondary"
						iconLeading={<Target03 className="h-full w-full" />}
						noTextPadding
						onClick={handleRecenter}
						size="sm"
						title="Recenter"
					/>
					<div className="relative flex flex-col overflow-hidden rounded-lg p-0.25 shadow-xs-skeuomorphic ring ring-1 ring-primary ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%">
						<Button
							className="size-8 rounded-b-none"
							color="secondary"
							iconLeading={<Plus className="h-full w-full" />}
							noBorders
							noTextPadding
							onClick={handleZoomIn}
							size="sm"
							title="Zoom In"
						/>
						<Button
							className="size-8 rounded-t-none border-t border-t-1 border-t-secondary_alt"
							color="secondary"
							iconLeading={<Minus className="h-full w-full" />}
							noBorders
							noTextPadding
							onClick={handleZoomOut}
							size="sm"
							title="Zoom Out"
						/>
					</div>
				</div>
			)}
		</div>
	);
};
